import { useTranslation } from "react-i18next";
import {
  EllipsisOutlined,
  EyeOutlined,
  UndoOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";

import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import DropdownMenu from "@/components_v2/DropdownMenu/DropdownMenu";
import ConfirmationModal from "@/components_v2/Modal/ConfirmationModal";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import { useEnrollmentAction } from "@/domains/certifications/hooks/useEnrollmentAction";
import {
  ADMIN_TABS,
  certificationPaths,
  certificationRouteKey,
} from "@/domains/certifications/routes";
import type { AdminOriginState } from "@/domains/certifications/types";
import { ParticipantActionsCellProps } from "@/features/certifications/participants/types";
import { ENROLLMENT_ACTIONS } from "@/domains/certifications/constants";

import "../styles.css";

const FROM_PARTICIPANTS: AdminOriginState = {
  fromTab: ADMIN_TABS.PARTICIPANTS,
};

const ParticipantActionsCell = ({
  certification,
  participant,
}: ParticipantActionsCellProps) => {
  const { t } = useTranslation();
  const { action, isBlockedByBan, openConfirm, confirmModalProps } =
    useEnrollmentAction({ certification, participant });

  return (
    <>
      <DropdownMenu
        content={
          <div>
            <BaseButton
              type={BUTTON_TYPES.LINK}
              className="dropdown-menu-button"
              isFullWidth
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.LINK}
              leadingIcon={<EyeOutlined />}
              to={certificationPaths.participant(
                certificationRouteKey(certification),
                participant.user.id,
              )}
              state={FROM_PARTICIPANTS}
            >
              {t("certification.admin.participants.view")}
            </BaseButton>

            {action && (
              <BaseTooltip
                title={
                  isBlockedByBan
                    ? t("certification.admin.participant.reinstate.banned")
                    : ""
                }
              >
                <BaseButton
                  className="dropdown-menu-button"
                  isFullWidth
                  size={BUTTON_SIZES.SMALL}
                  variant={BUTTON_VARIANTS.LINK}
                  disabled={isBlockedByBan}
                  leadingIcon={
                    action === ENROLLMENT_ACTIONS.REINSTATE ? (
                      <UndoOutlined />
                    ) : (
                      <UserDeleteOutlined />
                    )
                  }
                  onClick={openConfirm}
                >
                  {t(`certification.admin.participant.${action}.button`)}
                </BaseButton>
              </BaseTooltip>
            )}
          </div>
        }
        placement="bottom"
      >
        <BaseButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.LINK}
          leadingIcon={<EllipsisOutlined />}
          ariaLabel={t("certification.admin.participants.actionsLabel", {
            name: participant.user.fullName,
          })}
        />
      </DropdownMenu>

      <ConfirmationModal {...confirmModalProps} />
    </>
  );
};

export default ParticipantActionsCell;
